import { colors, fontSizes, spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { StyleSheet, type StyleProp, type TextStyle } from "react-native";

import { Box } from "../../../../../../mads";
import { Text } from "../../../../../../mads/Text";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { NexusJobSeekerDisclaimer } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

const DISCLAIMER_LINE_HEIGHT = fontSizes.f2 * 1.33;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: spacing.s4,
    paddingVertical: spacing.s3,
    marginTop: 8,
  },
  containerLessMargin: {
    marginTop: 4,
  },
  text: {
    lineHeight: DISCLAIMER_LINE_HEIGHT,
    color: colors.neutral1000,
  },
  textSimple: {
    color: colors.neutral700,
  },
  accommodationsSection: {
    marginTop: spacing.s3,
  },
  accommodationsLabel: {
    fontWeight: "bold",
  },
});

export type NexusJobDisclaimerProps = {
  accommodationEmail?: string;
  accommodationPhoneNumber?: string;
  accommodationUrl?: string;
  isSimpleVjImproveActive?: boolean;
  isCollapsedDescriptionDisclaimerBelow?: boolean;
  textSx?: StyleProp<TextStyle>;
};

export function NexusJobDisclaimer({
  accommodationEmail,
  accommodationPhoneNumber,
  accommodationUrl,
  isSimpleVjImproveActive = false,
  isCollapsedDescriptionDisclaimerBelow = false,
  textSx,
}: NexusJobDisclaimerProps) {
  const i18n = useI18n();

  const containerStyle = [
    styles.container,
    isSimpleVjImproveActive && isCollapsedDescriptionDisclaimerBelow && styles.containerLessMargin,
  ];

  const textStyle = textSx ?? [styles.text, isSimpleVjImproveActive && styles.textSimple];

  const accommodationText = getAccommodationText();

  return (
    <Box sx={containerStyle} testID="nexus-job-disclaimer">
      <Text level={2} sx={textStyle}>
        {i18n.gettext(
          "This employer uses Indeed, Inc. to generate an AI-powered summary evaluation of your application for this job. By applying, you agree that your application will include such a summary. If you do not agree, do not apply through Indeed. Instead, contact the employer directly to find another way to apply.",
        )}
      </Text>
      <Box sx={styles.accommodationsSection}>
        <Text level={2} sx={[textStyle, styles.accommodationsLabel]}>
          {i18n.pgettext("AI-powered summary evaluation - Accommodations label", "Accommodations")}
        </Text>
      </Box>
      <Box sx={styles.accommodationsSection}>
        <Text level={2} sx={textStyle}>
          {accommodationText}
        </Text>
      </Box>
    </Box>
  );

  function getAccommodationText(): string {
    const url = accommodationUrl;
    const phone = accommodationPhoneNumber;
    const email = accommodationEmail;

    if (url && phone && email) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0}, or at {1} or by email at {2}.",
        [url, phone, email],
      );
    } else if (url && phone) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0} or at {1}.",
        [url, phone],
      );
    } else if (url && email) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0} or by email at {1}.",
        [url, email],
      );
    } else if (phone && email) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0} or by email at {1}.",
        [phone, email],
      );
    } else if (url) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0}.",
        [url],
      );
    } else if (phone) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer at {0}.",
        [phone],
      );
    } else if (email) {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer by email at {0}.",
        [email],
      );
    } else {
      return i18n.gettext(
        "If you would like to request an accommodation, contact the employer directly.",
      );
    }
  }
}

export function OGWrappedNexusJobDisclaimer() {
  const ogModel = useViewjobOneGraphModel();
  const nexusDisclaimer = ogModel?.nexusDisclaimer;
  const isNexusDisclaimerActive = useViewjobProctorAllocation(NexusJobSeekerDisclaimer).value > 0;

  if (!isNexusDisclaimerActive || !nexusDisclaimer?.showNexusDisclaimer) {
    return null;
  }

  return (
    <NexusJobDisclaimer
      accommodationEmail={nexusDisclaimer.accommodationEmail}
      accommodationPhoneNumber={nexusDisclaimer.accommodationPhoneNumber}
      accommodationUrl={nexusDisclaimer.accommodationUrl}
      isSimpleVjImproveActive={ogModel?.features?.isSimpleVjImproveActive}
    />
  );
}
